<template>
  <div ref="selectorRef"
       class="lang-selector relative inline-block font-sans text-[14px]">
    <button 
      class="lang-btn group flex items-center gap-1.5 px-3 py-1.5 transition-all duration-200"
      @click="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
      @keydown.escape="close"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      :aria-label="$t('languageSelector.filterLabel', 'Фільтр за мовою')">
      <!-- <span class="text-gray-400 font-medium tracking-tight">{{ $t('courseLangSelector.label') }}</span> -->
      <span class="text-black text-[16px]">{{ current.label }}</span>
      <span v-if="current.count !== undefined && current.count !== null"
            class="text-black text-[16px] tabular-nums"
            style="font-family: 'Roboto', sans-serif;">({{ current.count }})</span>
      
      <svg 
        class="w-5 h-5 ml-0.5 text-black transition-transform duration-300 ease-out"
        :class="{ 'rotate-180': isOpen }"
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        stroke-width="2.5"
        aria-hidden="true">
        <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>

    <Teleport to="body">
      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0 scale-95 -translate-y-2"
        enter-to-class="opacity-100 scale-100 translate-y-0"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100 scale-100 translate-y-0"
        leave-to-class="opacity-0 scale-95 translate-y-2">
        <div 
          v-if="isOpen"
          class="lang-dropdown fixed w-72 bg-white border border-gray-100 rounded-lg shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] pt-2 pb-8 z-50 overflow-y-auto overflow-x-hidden cursor-pointer"
          :style="dropdownStyle"
          role="listbox"
          @keydown.escape="close"
          @keydown.arrow-down.prevent="navigateDown"
          @keydown.arrow-up.prevent="navigateUp"
          @keydown.enter.prevent="selectFocused">
          <button
            v-for="{ code, label, count } in others"
            :key="code ?? 'all'"
            @click="select(code)"
            @mouseenter="focusedIndex = others.findIndex(l => l.code === code)"
            class="w-full flex justify-between items-center px-[34px] py-3 transition-colors duration-200 cursor-pointer"
            :class="[
              code === props.modelValue ? 'bg-gray-50' : 'hover:bg-gray-50',
              { 
                'bg-gray-50': focusedIndex === others.findIndex(l => l.code === code) && code === props.modelValue,
                'bg-[rgba(0,206,155,0.05)]': focusedIndex === others.findIndex(l => l.code === code) && code !== props.modelValue
              }
            ]"
            role="option"
            :aria-selected="code === props.modelValue">
            <span 
              class="text-[16px] transition-colors text-left"
              :class="[
                code === props.modelValue ? 'font-bold text-black' : 'text-black',
                { 'text-[#00CE9B]': focusedIndex === others.findIndex(l => l.code === code) && code !== props.modelValue }
              ]">
              {{ label }}
            </span>
            <span 
              v-if="count !== undefined && count !== null"
              class="text-[16px] tabular-nums transition-colors"
              style="font-family: 'Roboto', sans-serif;"
              :class="[
                code === props.modelValue ? 'text-black' : 'text-black',
                { 'text-[#00CE9B]': focusedIndex === others.findIndex(l => l.code === code) && code !== props.modelValue }
              ]">
              {{ count }}
            </span>
          </button>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { getLocaleEndonym } from '@/utils/locale'
import type { ContentLanguage } from '@/domain/entities/content-language.entity'
import { useDropdown } from '@/composables/ui'

interface Props {
  availableLocales?: Array<string | ContentLanguage> // Поддержка старого формата (string[]) и нового (ContentLanguage[])
  modelValue?: string | null // Выбранный язык фильтра (null = все языки)
}

interface Emits {
  (e: 'update:modelValue', value: string | null): void
}

const props = withDefaults(defineProps<Props>(), {
  availableLocales: () => [],
  modelValue: null,
})

const emit = defineEmits<Emits>()
const { t } = useI18n()

const isOpen = ref(false)
const selectorRef = ref<HTMLElement | null>(null)
const focusedIndex = ref(-1)

// Используем composable для управления dropdown
const { dropdownStyle } = useDropdown(isOpen, selectorRef, {
  triggerSelector: '.lang-selector',
  dropdownSelector: '.lang-dropdown',
  dropdownHeight: 200,
  offset: 8,
  itemHeight: 40, // Высота одного элемента списка (py-3 = 12px сверху + 12px снизу + ~16px текста)
  minHeight: 100,
  maxHeight: 400,
})

// Вычисляем общее количество курсов для опции "Все"
const totalCoursesCount = computed(() => {
  return props.availableLocales.reduce((total, item) => {
    if (typeof item === 'string') {
      return total
    }
    return total + (item.courseCount || 0)
  }, 0)
})

// Добавляем опцию "Все" в начало списка
const languages = computed(() => {
  const allOption = {
    code: null,
    label: t('courseLangSelector.all'),
    count: totalCoursesCount.value > 0 ? totalCoursesCount.value : undefined
  }
  const localeOptions = props.availableLocales.map((item) => {
    // Поддержка старого формата (string) и нового (ContentLanguage)
    if (typeof item === 'string') {
      return {
        code: item,
        label: getLocaleEndonym(item),
        count: undefined
      }
    } else {
      return {
        code: item.id,
        label: item.endonym,
        count: item.courseCount
      }
    }
  })
  return [allOption, ...localeOptions]
})

const current = computed(() => {
  if (props.modelValue === null) {
    return {
      label: t('courseLangSelector.all'),
      count: totalCoursesCount.value > 0 ? totalCoursesCount.value : undefined
    }
  }
  // Ищем в уже обработанном списке languages
  const selected = languages.value.find((l) => l.code === props.modelValue)
  if (selected) {
    return { label: selected.label, count: selected.count }
  }
  return { label: getLocaleEndonym(props.modelValue), count: undefined }
})

const others = computed(() => languages.value)

const close = () => {
  isOpen.value = false
  focusedIndex.value = -1
}

const toggle = () => {
  isOpen.value = !isOpen.value
  if (!isOpen.value) {
    focusedIndex.value = -1
  }
}

const select = (code: string | null) => {
  emit('update:modelValue', code)
  close()
}

const navigateDown = () => {
  if (focusedIndex.value < others.value.length - 1) {
    focusedIndex.value++
  } else {
    focusedIndex.value = 0
  }
}

const navigateUp = () => {
  if (focusedIndex.value > 0) {
    focusedIndex.value--
  } else {
    focusedIndex.value = others.value.length - 1
  }
}

const selectFocused = () => {
  if (focusedIndex.value >= 0 && focusedIndex.value < others.value.length) {
    const option = others.value[focusedIndex.value]
    if (option) {
      select(option.code)
    }
  }
}

// Логика управления dropdown (позиционирование, клики вне) вынесена в useDropdown composable
</script>

<style scoped>
.lang-dropdown {
  font-family: var(--font-inter);
}

.lang-btn {
  /* border: 2px solid #E8E8E8; */
  background-color: transparent;
  color: var(--color-grey);
  transition: background-color 0.3s ease-in-out, all 0.2s;
  padding: 18px 20px;
  cursor: pointer;
  border-radius: 5px;
  font-family: var(--font-inter);
}

.lang-btn:hover {
  background-color: #F9FAFB;
  border-radius: 5px;
}
</style>
